/*
 * Copyright (c) 2024-2025 Nodals AI Ltd
 * All rights reserved.
 */

import { GlobalFactStore } from './rules/facts/store';
import type { PrebidApi } from './types/prebid';

interface Version {
  major: number;
  minor: number;
  patch: number;
  preRelease: number | string;
}

declare const __DEBUG_BUILD__: boolean;

const parseVersion = (version: string): Version => {
  let major = 0;
  let minor = 0;
  let patch = 0;
  let main = '';
  let preRelease: string | undefined;
  if (version) {
    [main, preRelease] = version.split('-');
    [major, minor, patch] = main.split('.').map(Number);
  }
  return {
    major: isNaN(major) ? 0 : major,
    minor: minor === undefined ? 0 : minor,
    patch: patch === undefined ? 0 : patch,
    preRelease: preRelease ? preRelease : '0',
  };
};

const compareVersions = (version1: string, version2: string): string => {
  const v1 = parseVersion(version1);
  const v2 = parseVersion(version2);

  if (v1.major !== v2.major) {
    return v1.major > v2.major ? version1 : version2;
  }
  if (v1.minor !== v2.minor) {
    return v1.minor > v2.minor ? version1 : version2;
  }
  if (v1.patch !== v2.patch) {
    return v1.patch > v2.patch ? version1 : version2;
  }
  if (v1.preRelease !== v2.preRelease) {
    return v1.preRelease > v2.preRelease ? version1 : version2;
  }
  return version1;
};

const generateUUID = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const isGptAvailable = (): boolean => {
  if (window.googletag && typeof window.googletag.pubads === 'function') {
    return true;
  }
  return false;
};

const isAstAvailable = (): boolean => {
  if (window.apntag && typeof window.apntag.getAstVersion === 'function') {
    return true;
  }
  return false;
};

const deepMerge = (target: any, source: any): any => {
  Object.keys(source).forEach((key) => {
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
      target[key] = deepMerge(target[key] || {}, source[key]);
    } else {
      target[key] = source[key];
    }
  });

  return target;
};

const clone = <T>(obj: T): T => {
  return typeof structuredClone === 'function'
    ? structuredClone(obj)
    : JSON.parse(JSON.stringify(obj));
};

const ensureStringArray = (input?: unknown[]): string[] => {
  if (Array.isArray(input)) {
    return input.map(String);
  }
  return [];
};

const getGlobalPrebidObj = (): PrebidApi | undefined => {
  // @ts-ignore
  const globalsArray: string[] = window._pbjsGlobals;
  if (Array.isArray(globalsArray) && globalsArray.length > 0) {
    // @ts-ignore
    return window[globalsArray[0]] as PrebidApi;
  }
  return undefined;
};

const getPrebidUserId = (source: string): string | undefined => {
  const prebid = getGlobalPrebidObj();
  if (!prebid || typeof prebid.getUserIdsAsEids !== 'function') {
    return undefined;
  }

  try {
    const eids = prebid.getUserIdsAsEids();
    if (!Array.isArray(eids)) {
      return undefined;
    }

    const matchingEid = eids.find(eid => eid.source === source);
    if (matchingEid && Array.isArray(matchingEid.uids) && matchingEid.uids.length > 0) {
      return matchingEid.uids[0].id;
    }
  } catch (error) {
    // Silently handle any errors from Prebid API
  }

  return undefined;
};

const debugPixel = (params: Record<string, string>): void => {
  if (!__DEBUG_BUILD__) {
    return;
  }
  const globalFacts = GlobalFactStore.getInstance().getFacts();
  let referrer = document.referrer;
  try {
    referrer = (top?.location.href as string) || referrer;
  } catch (e) {}
  params.referrer = referrer;
  params.br = globalFacts['browser.name'] ?? '';
  params.os = globalFacts['device.os'] ?? '';
  params.rnd = String(Math.random());
  const query = new URLSearchParams(params).toString();
  const img = new Image();
  img.src = `https://nodals.io/d/v1/dbg?${query}`;
};

const fetchJson = <T = unknown>(url: string, options?: RequestInit): Promise<T> => {
  return fetch(url, options).then((response) => {
    if (!response.ok) {
      return response.text().then((body) => {
        throw new Error(`Fetch failed with status ${response.status}: ${body}`);
      });
    }
    return response.json() as Promise<T>;
  });
};

const fillTemplate =
  (strings: TemplateStringsArray, ...keys: string[]) =>
  (values: Record<string, string>): string =>
    strings.reduce((result, str, i) => {
      const key = keys[i - 1];
      return result + (key ? values[key] || '' : '') + str;
    });

export {
  clone,
  compareVersions,
  debugPixel,
  deepMerge,
  ensureStringArray,
  fetchJson,
  fillTemplate,
  generateUUID,
  getGlobalPrebidObj,
  getPrebidUserId,
  isAstAvailable,
  isGptAvailable,
};
